import {
  borderRadius,
  customScrollMinimal,
  SkeletonLoader,
  TabLines,
  typographyV2,
} from '@toggle/design-system';
import styled from 'styled-components';

export const Container = styled.section`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  border: 1px solid var(--border-soft);
  ${borderRadius.r2};
  overflow: hidden;
`;

export const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 16px 16px 8px 16px;
`;

export const Title = styled.h3`
  ${typographyV2.titleMd};
  color: var(--text-default);
`;

export const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

export const StyledTabLines = styled(TabLines)`
  ${TabLines.Styled.Header} {
    padding: 0 20px;
    border-bottom: solid 1px var(--border-default);
  }

  ${TabLines.Styled.Tab} {
    margin-right: 16px;

    &:last-child {
      margin-right: 0;
    }
  }

  ${TabLines.Styled.TabButton} {
    white-space: nowrap;
    ${typographyV2.uiSm};
    padding: 8px 4px;
  }
`;

export const FeedWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  ${customScrollMinimal};
`;

export const DateHeader = styled.div`
  padding: 4px 20px;
  background: var(--surface-default);
`;

export const DateText = styled.p`
  ${typographyV2.bodySm};
  color: var(--text-soft);
`;

export const FeedItemWrapper = styled.div<{ $isLast?: boolean }>`
  border-bottom: ${({ $isLast }) =>
    $isLast ? 'none' : '1px solid var(--border-soft)'};
  cursor: pointer;
`;

export const FeedItem = styled.div<{ $isRead?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 20px;

  &:hover {
    background-color: var(--surface-hover);
  }
`;

export const FeedItemHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

export const TypeIndicator = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

export const IconWrapper = styled.div<{ $bgColor: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  ${borderRadius.rFull}
  padding: 2px;
  background-color: ${({ $bgColor }) => $bgColor};
`;

export const TypeLabel = styled.span<{ $color: string; $isRead?: boolean }>`
  ${typographyV2.uiXs};
  color: ${({ $color, $isRead }) => ($isRead ? 'var(--text-soft)' : $color)};
`;

export const InsightTitle = styled.p<{ $isRead?: boolean }>`
  ${typographyV2.titleBase};
  color: ${({ $isRead }) =>
    $isRead ? 'var(--text-soft)' : 'var(--text-default)'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

export const StatsRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
`;

export const StatsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

export const StatLabel = styled.span`
  ${typographyV2.bodySm};
  color: var(--text-soft);
`;

export const StatValue = styled.span`
  ${typographyV2.bodySm};
  color: var(--text-default);
`;

export const PriceValue = styled.span`
  ${typographyV2.bodySm};
  font-family: var(--font-roboto-mono);
  color: var(--text-default);
`;

export const BullishValue = styled.span`
  ${typographyV2.bodySm};
  font-family: var(--font-roboto-mono);
  color: var(--viz-bullish);
`;

export const BearishValue = styled.span`
  ${typographyV2.bodySm};
  font-family: var(--font-roboto-mono);
  color: var(--viz-bearish);
`;

export const BullishText = styled.span`
  ${typographyV2.bodySm};
  color: var(--viz-bullish);
`;

export const BearishText = styled.span`
  ${typographyV2.bodySm};
  color: var(--viz-bearish);
`;

export const ScheduledLabel = styled.span`
  ${typographyV2.bodySm};
  color: var(--text-soft);
`;

export const ScheduledDate = styled.span`
  ${typographyV2.titleSm};
  font-weight: 500;
  color: var(--text-default);
`;

export const TimeAgo = styled.span`
  ${typographyV2.bodyXs};
  color: var(--text-soft);
  white-space: nowrap;
`;

export const VerticalDivider = styled.div`
  width: 1px;
  height: 12px;
  background-color: var(--border-soft);
`;

export const Dot = styled.div`
  width: 2px;
  height: 2px;
  ${borderRadius.rFull}
  background-color: var(--text-soft);
`;

export const StarsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 2px;
`;

export const StarsLabel = styled.span`
  ${typographyV2.bodySm};
  color: var(--text-default);
`;

export const EpisodesLabel = styled.span`
  ${typographyV2.bodyXs};
  color: var(--text-default);
`;

export const StyledLoader = styled(SkeletonLoader).attrs({
  areas: [
    {
      styles: {
        width: '100%',
        height: '100%',
        borderRadius: '8px',
      },
    },
  ],
})`
  width: 100%;
  height: 100%;
  padding: 20px;
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
`;

export const EmptyStateText = styled.p`
  ${typographyV2.bodyBase};
  color: var(--text-soft);
  text-align: center;
`;

export const EndOfFeedWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 20px;
`;

export const EndOfFeedLine = styled.div`
  height: 1px;
  background-color: var(--border-soft);
  flex: 1;
`;

export const EndOfFeedLabel = styled.span`
  ${typographyV2.bodyBase};
  color: var(--text-disabled);
  white-space: nowrap;
`;

export const EarningsLabelWrapper = styled.div`
  display: flex;
  gap: 4px;
`;
